import { IS_DEBUG } from '@common/constants/is-debug.constant';
import { InlineJsonPreview } from '@common/general-ui/json-preview/json-preview.component';
import { IUseCustomFormReturn } from '@common/hooks/use-form.hook';
import React, { ChangeEventHandler, FormEventHandler, ReactNode, useCallback, useMemo } from 'react';
import { FieldValues, FormProvider, SubmitHandler, UnpackNestedValue } from 'react-hook-form';

// For some components need trigger validate event manually, so added additional context.
export interface IFormAdditionalContextProps<T> {
    registerBlurHandler: (name: keyof T, handler: ChangeEventHandler) => void;
    onFieldBlur: (name: keyof T) => void;
    onFieldChange: (name: keyof T) => void;
}

export const FormAdditionalContext = React.createContext<IFormAdditionalContextProps<unknown>>({
    registerBlurHandler: () => null,
    onFieldBlur: () => null,
    onFieldChange: () => null,
});

interface IProps<T> {
    form: IUseCustomFormReturn<T>;
    children: ReactNode;
    className?: string;
}

export function Form<T extends FieldValues>({
    children,
    form: {
        formContext,
        setBlurForAllFields,
        isValid,
        props: { onFieldBlur, onFieldChange, onSubmit },
        _onBlurHandlers,
    },
    className,
}: IProps<T>): JSX.Element {
    const handleSubmit = useCallback<SubmitHandler<T>>(
        (data: UnpackNestedValue<T>) => {
            onSubmit(data);
        },
        [onSubmit],
    );

    const renderJsonPreview = (): JSX.Element => {
        const json = {
            values: formContext.getValues(),
            errors: formContext.formState.errors,
            touched: formContext.formState.touchedFields,
            formState: formContext.formState,
            isValid,
        };
        return <InlineJsonPreview json={json} collapsed_json={false} />;
    };

    const registerBlurHandler = useCallback(
        (fieldName: keyof T, handler: ChangeEventHandler) => {
            _onBlurHandlers[fieldName as string] = handler;
        },
        [_onBlurHandlers],
    );

    const onFieldBlurHandler = useCallback(
        (fieldName: keyof T) => {
            onFieldBlur && onFieldBlur(fieldName, formContext.getValues() as T);
        },
        [onFieldBlur, formContext],
    );

    const onFieldChangeHandler = useCallback(
        (fieldName: keyof T) => {
            onFieldChange && onFieldChange(fieldName, formContext.getValues() as T);
        },
        [formContext, onFieldChange],
    );

    const additionalContext: IFormAdditionalContextProps<T> = useMemo(() => {
        return {
            registerBlurHandler,
            onFieldBlur: onFieldBlurHandler,
            onFieldChange: onFieldChangeHandler,
        };
    }, [onFieldBlurHandler, onFieldChangeHandler, registerBlurHandler]);

    const handleFormSubmit = useCallback<FormEventHandler>(
        (e: React.MouseEvent) => {
            // scroll to first error
            if (formContext.formState.errors) {
                const errors = Object.keys(formContext.formState.errors).map((key) => key);
                const inputElement = document.querySelector(`input[name="${errors[0]}"]`);
                inputElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
            e.preventDefault();
            setBlurForAllFields();

            formContext.handleSubmit(handleSubmit)();
        },
        [formContext, handleSubmit, setBlurForAllFields],
    );

    return (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <FormProvider {...(formContext as any)}>
            <FormAdditionalContext.Provider value={additionalContext}>
                {IS_DEBUG && renderJsonPreview()}
                <form autoComplete="off" noValidate className={className} onSubmit={handleFormSubmit}>
                    {children}
                </form>
            </FormAdditionalContext.Provider>
        </FormProvider>
    );
}
